import { type Ref, useLayoutEffect, useRef } from 'react'

import { useMediaQuery } from '@/lib/useMediaQuery'
import { cn } from '@/lib/utils'
import { useLandingLocale } from '@/navigation/useLandingLocale'
import { article as articleUi } from '@/shared/lib/articleStyles'

import { type DemoRendition, LANDING_DEMOS, type LandingDemoId, demoVideoSources } from './catalog'
import { LANDING_MD_MEDIA_QUERY, applyDemoPlaybackCursor, readDemoPlaybackCursor } from './syncVisibleDemoPlayback'
import { buildVideoObjectJsonLd } from './videoObject'

type LandingDemoPlayerProps = {
    demoId: LandingDemoId
    variant?: 'landing' | 'article' | 'modal'
    className?: string
}

function disableTextTracks(video: HTMLVideoElement) {
    for (const track of video.textTracks) {
        track.mode = 'disabled'
    }
}

function RenditionVideo({
    rendition,
    captionsPath,
    ariaLabel,
    className,
    videoRef,
    onLoadedMetadata,
}: {
    rendition: DemoRendition
    captionsPath: string
    ariaLabel: string
    className?: string
    videoRef?: Ref<HTMLVideoElement>
    onLoadedMetadata: (video: HTMLVideoElement) => void
}) {
    return (
        <video
            ref={videoRef}
            className={className}
            poster={rendition.poster}
            controls
            playsInline
            preload='metadata'
            width={rendition.width}
            height={rendition.height}
            aria-label={ariaLabel}
            onLoadedMetadata={event => onLoadedMetadata(event.currentTarget)}
        >
            {demoVideoSources(rendition).map(source => (
                <source key={source.type} src={source.src} type={source.type} />
            ))}
            <track kind='captions' srcLang='es' src={captionsPath} label='Español' />
        </video>
    )
}

export function LandingDemoPlayer({ demoId, variant = 'landing', className }: LandingDemoPlayerProps) {
    const locale = useLandingLocale()
    const demo = LANDING_DEMOS[demoId]
    const lang = locale === 'en' ? 'en' : 'es'
    const jsonLd = buildVideoObjectJsonLd(demo, locale)
    const isArticle = variant === 'article'
    const isModal = variant === 'modal'
    const isDesktop = useMediaQuery(LANDING_MD_MEDIA_QUERY)
    const rendition = isDesktop && demo.landscape ? demo.landscape : demo.portrait
    const showLandscape = rendition === demo.landscape
    const videoRef = useRef<HTMLVideoElement>(null)
    const playbackCursorRef = useRef({ time: 0, playing: false })

    useLayoutEffect(() => {
        const video = videoRef.current
        return () => {
            if (video) playbackCursorRef.current = readDemoPlaybackCursor(video)
        }
    }, [rendition.hevc])

    const videoClass = isArticle
        ? 'block h-auto w-full rounded-article-frame-lg bg-article-shell'
        : 'block h-auto w-full rounded-[22px] bg-[#17130E]'

    const frameClass = showLandscape
        ? 'w-full'
        : isArticle
          ? articleUi.figureVideoPortrait
          : isModal
            ? 'w-full'
            : 'mx-auto w-full max-w-[280px] md:max-w-[320px]'

    return (
        <figure className={cn(isArticle ? articleUi.figureVideo : 'm-0 w-full', className)}>
            <script type='application/ld+json'>{JSON.stringify(jsonLd)}</script>
            <div className={frameClass}>
                <RenditionVideo
                    key={rendition.hevc}
                    rendition={rendition}
                    captionsPath={demo.captionsPath}
                    ariaLabel={demo.name[lang]}
                    className={videoClass}
                    videoRef={videoRef}
                    onLoadedMetadata={video => {
                        disableTextTracks(video)
                        applyDemoPlaybackCursor(video, playbackCursorRef.current)
                    }}
                />
            </div>
        </figure>
    )
}
